import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, User, LogOut, MessageSquare, Sun, Moon, Crown, Sparkles } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useTranslation } from '../I18nProvider'
import { useThemeStore } from '../../store/themeStore'
import { useUsageStore } from '../../store/usageStore'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { user, isAuthenticated, logout } = useAuthStore()
  const { isDark, toggleTheme } = useThemeStore()
  const { isPremium } = useUsageStore()
  const location = useLocation()
  const { t } = useTranslation()

  const navigation = [
    { name: 'Discussion', href: '/about', icon: <MessageSquare className="w-4 h-4" /> },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Available Conversions', href: '/#supportedconversions' },
    { name: 'FAQ', href: '/#faq' },
  ]

  const handleLogout = () => {
    logout()
    setIsMenuOpen(false)
  }

  return (
    <nav className="bg-[#111827] border-b border-gray-800 sticky top-0 z-50 w-full text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                <span className="text-white font-black text-xl">H</span>
              </div>
              <span className="text-xl font-bold tracking-tight">Joy File Convert</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-white transition-colors`}
              >
                {item.icon}
                {item.name}
              </Link>
            ))}
            
            <div className="h-6 w-px bg-gray-800" />

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-all duration-300 ${
                isDark 
                  ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {isPremium() && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 rounded-lg">
                <Crown className="w-4 h-4 text-yellow-500 fill-current" />
                <span className="text-[10px] font-black text-yellow-500 uppercase tracking-widest">Pro User</span>
              </div>
            )}

            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard" className="text-sm font-medium text-gray-400 hover:text-white">
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-sm font-medium text-gray-400 hover:text-white">
                  Sign In
                </Link>
                <Link to="/pricing" className="bg-primary-600 hover:bg-primary-500 px-6 py-2 rounded-xl text-sm font-bold transition-all shadow-lg shadow-primary-900/20">
                  Get the app
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#111827] border-b border-gray-800 animate-fade-in">
          <div className="px-4 pt-2 pb-6 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block px-4 py-3 text-base font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-800 space-y-4">
              <button
                onClick={toggleTheme}
                className={`flex items-center justify-between w-full px-4 py-3 rounded-xl font-medium transition-all ${
                  isDark ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {isPremium() && (
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Crown className="w-5 h-5 text-yellow-500 fill-current" />
                    <span className="font-bold text-yellow-500 uppercase tracking-widest text-sm">Pro Membership Active</span>
                  </div>
                  <Sparkles className="w-4 h-4 text-yellow-500/50" />
                </div>
              )}

              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl font-bold"
                >
                  Sign Out
                </button>
              ) : (
                <Link
                  to="/pricing"
                  className="block w-full bg-primary-600 text-center text-white px-4 py-3 rounded-xl font-bold shadow-lg shadow-primary-900/20"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get the app
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
